import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { devError } from "@/utils/logger";
import { Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATIENT_OPTIONS = ["Yes", "No", "Prefer not to say"];

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 font-body text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 min-h-[44px]";

function TextField({ label, hint, testId, ...props }) {
  return (
    <div>
      <label className="block font-body text-sm font-medium text-navy mb-1">
        {label} {hint && <span className="text-gray-500">{hint}</span>}
      </label>
      <input type="text" className={inputCls} data-testid={testId} {...props} />
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div>
      <label className="block font-body text-sm font-medium text-navy mb-2">Your Rating *</label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button key={star} type="button" onClick={() => onChange(star)}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center" data-testid={`star-btn-${star}`}>
            <Star className={`w-7 h-7 transition-colors ${star <= value ? "fill-brand-red text-brand-red" : "text-gray-500"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

function MemberPicker({ value, onChange }) {
  return (
    <div>
      <label className="block font-body text-sm font-medium text-navy mb-2">Are you a community member?</label>
      <div className="flex gap-2 flex-wrap">
        {PATIENT_OPTIONS.map((opt) => (
          <button key={opt} type="button" onClick={() => onChange(opt)}
            className={`px-4 py-2 rounded-full text-sm font-body border transition-colors min-h-[44px] ${
              value === opt ? "bg-navy text-white border-navy" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
            data-testid={`patient-option-${opt.toLowerCase().replace(/ /g, "-")}`}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function useReviewForm(initialBusiness, onSuccess) {
  const [form, setForm] = useState({
    businessName: initialBusiness,
    starRating: 5,
    title: "",
    body: "",
    topItem: "",
    reviewerName: "",
    isIwmcPatient: "Prefer not to say",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.businessName || !form.title || form.body.length < 20) {
      setError("Please fill in all required fields. Review must be at least 20 characters.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/reviews`, form);
      onSuccess?.();
    } catch (submitError) {
      devError("Review submission failed:", submitError);
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const field = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  return { error, field, form, handleSubmit, loading };
}

function ReviewFields({ field, form }) {
  return <>
    <TextField label="Business Name *" required value={form.businessName} onChange={(event) => field("businessName", event.target.value)} placeholder="e.g. Griffy's Organics" testId="review-business-name" />
    <StarPicker value={form.starRating} onChange={(value) => field("starRating", value)} />
    <TextField label="Review Headline *" required value={form.title} onChange={(event) => field("title", event.target.value)} placeholder="Summarize your experience" testId="review-title" />
    <div><label className="block font-body text-sm font-medium text-navy mb-1">Your Review * (min 20 chars)</label><textarea required minLength={20} rows={4} value={form.body} onChange={(event) => field("body", event.target.value)} placeholder="Share your experience in detail..." className="w-full border border-gray-200 rounded-lg px-3 py-2.5 font-body text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 resize-none" data-testid="review-body" /></div>
    <TextField label="Top item you ordered or tried" hint="(optional)" value={form.topItem} onChange={(event) => field("topItem", event.target.value)} placeholder="e.g. Green smoothie bowl" />
    <TextField label="Your Name" hint="(optional — defaults to Anonymous)" value={form.reviewerName} onChange={(event) => field("reviewerName", event.target.value)} placeholder="Your name" />
    <MemberPicker value={form.isIwmcPatient} onChange={(value) => field("isIwmcPatient", value)} />
  </>;
}

export function ReviewForm({ initialBusiness = "", onSuccess }) {
  const { error, field, form, handleSubmit, loading } = useReviewForm(initialBusiness, onSuccess);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <ReviewFields field={field} form={form} />
      {error && <p className="text-brand-red text-sm font-body" data-testid="review-error">{error}</p>}
      <button type="submit" disabled={loading} className="w-full bg-navy hover:bg-navy/90 text-white py-3 rounded-lg font-body font-medium text-sm transition-colors disabled:opacity-50 min-h-[44px]" data-testid="review-submit-button">{loading ? "Submitting..." : "Submit Review"}</button>
    </form>
  );
}

export default function ReviewModal({ open, onClose, listingName = "" }) {
  const [submitted, setSubmitted] = useState(false);

  const handleSuccess = () => setSubmitted(true);
  const handleClose = () => {
    setSubmitted(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-navy">
            Leave a Review
          </DialogTitle>
          {listingName && (
            <p className="font-body text-sm text-gray-500 mt-1">for <span className="font-medium text-navy">{listingName}</span></p>
          )}
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <div className="text-5xl mb-4">✓</div>
            <h3 className="font-heading text-2xl text-navy mb-2">Thank You!</h3>
            <p className="font-body text-gray-600">Our team reviews submissions within 48 hours.</p>
            <button
              onClick={handleClose}
              className="mt-6 bg-navy text-white px-6 py-2.5 rounded-lg font-body text-sm hover:bg-navy/90 transition-colors"
              data-testid="review-close-button"
            >
              Close
            </button>
          </div>
        ) : (
          <ReviewForm initialBusiness={listingName} onSuccess={handleSuccess} />
        )}
      </DialogContent>
    </Dialog>
  );
}
